/**
 * Admin feature routes — the admin module's contribution to the authenticated
 * route tree (see src/features/routes-registry.ts for the contract).
 *
 * Admin owns the whole `/admin` subtree. The structure is a NESTED LAYOUT ROUTE
 * (mirrors andavo-dev's AdminNavLayout / SettingsLayout pattern):
 *
 *   <Route path="admin" element={<AdminLayout/>}>   ← layout: AdminGuard + sub-nav + <Outlet>
 *     <Route index element={<CommandCenterPage/>} />
 *     <Route path="ops-dashboard" element={<OpsDashboardPage/>} />
 *     … 8 more child pages …
 *   </Route>
 *
 * Why this shape (the two security-critical decisions from rebuild-lessons.md):
 *   - The fail-closed admin gate (AdminGuard) lives ONCE, in AdminLayout, so all
 *     10 pages are gated by a single check — not 10 per-page re-implementations
 *     (lesson #2: grade.html failed OPEN on its own bespoke admin check). A
 *     non-admin or any error never reaches a child <Route>.
 *   - No page boots MSAL or reads a token directly; all data flows through the
 *     shell's one configureApi resolver via the vendored `api` client.
 *
 * Everything renders inside the shell's ProtectedRoute + ExpertsNavLayout
 * <Outlet> (the shell guarantees the user is authenticated before AdminLayout
 * even mounts). Paths are relative to the authenticated root '/'.
 */
import { Navigate, Route } from 'react-router';
import type { FeatureRouteList } from '@/features/routes-registry';
import { AdminLayout } from './components/AdminLayout';
import {
  AccessRequestsPage,
  ImprovementsPage,
  CommandCenterPage,
  CronAuditPage,
  FeatureFlagsPage,
  GradePage,
  KbIngestPage,
  LlmBudgetPage,
  OpsDashboardPage,
  SessionViewPage,
  StatusPage,
  SystemInfoPage,
  UsersPage,
} from './pages';

export const adminRoutes: FeatureRouteList = [
  <Route element={<AdminLayout />} key="admin" path="admin">
    {/* /admin lands on user access — the day job. The Command Center dashboard
        it used to land on is still here, one click away under System > Overview.
        `replace` keeps /admin out of the history stack so Back doesn't bounce. */}
    <Route
      element={<Navigate replace to="/admin/users" />}
      index
      key="admin-index"
    />
    <Route
      element={<CommandCenterPage />}
      key="admin-command-center"
      path="command-center"
    />
    <Route
      element={<OpsDashboardPage />}
      key="admin-ops"
      path="ops-dashboard"
    />
    <Route
      element={<SystemInfoPage />}
      key="admin-sysinfo"
      path="system-info"
    />
    <Route
      element={<FeatureFlagsPage />}
      key="admin-flags"
      path="feature-flags"
    />
    <Route element={<CronAuditPage />} key="admin-cron" path="cron-audit" />
    <Route element={<LlmBudgetPage />} key="admin-llm" path="llm-budget" />
    <Route element={<KbIngestPage />} key="admin-kb" path="kb-ingest" />
    <Route element={<GradePage />} key="admin-grade" path="grade" />
    <Route
      element={<SessionViewPage />}
      key="admin-session"
      path="session-view"
    />
    <Route element={<StatusPage />} key="admin-status" path="status" />
    <Route
      element={<AccessRequestsPage />}
      key="admin-access-requests"
      path="access-requests"
    />
    <Route
      element={<ImprovementsPage />}
      key="admin-improvements"
      path="improvements"
    />
    <Route element={<UsersPage />} key="admin-users" path="users" />
  </Route>,
];
